import { AxiosResponse } from 'axios';

export type ResultValue = unknown; // DEPRECATED
export type ResultItem = Record<string, unknown>;

// inspired by https://www.geeksforgeeks.org/how-to-create-and-download-csv-file-in-javascript/
export function convertResultsToCsv(
  results: ResultItem[],
  separator = ',',
): string {
  const csvRows = [];
  const headers = Object.keys(results[0]);
  csvRows.push(headers.map(getCsvCell).join(separator));
  // make sure to align values with respect to headers
  for (const row of results) {
    const values = headers.map((header) => row[header]);
    csvRows.push(values.map(getCsvCell).join(separator));
  }
  return csvRows.join('\n');
}

/**
 * Get a number value of param or an em dash character. Look the tests for details
 */
export function getNumCell(resultValue?: unknown): '—' | number {
  if (typeof resultValue === 'object') return '—';
  resultValue = parseFloat(resultValue as string);
  if (typeof resultValue === 'number' && !Number.isNaN(resultValue)) {
    return resultValue;
  }
  return '—';
}

/**
 * Get a string value of param or an em dash character. Look the tests for details
 */
export function getStrCell(resultValue?: unknown): string {
  if (typeof resultValue === 'number' && !Number.isNaN(resultValue)) {
    return resultValue.toString();
  }
  if (!resultValue) return '—';
  if (typeof resultValue === 'string') return resultValue;
  return '';
}

// inspired by https://stackoverflow.com/a/56370447/1054723
function getCsvCell(resultValue?: unknown): string {
  let csvCell = getStrCell(resultValue);
  csvCell = csvCell
    // remove multiple spaces and jumpline (breaks csv)
    .replace(/(\r\n|\n|\r)/gm, '')
    .replace(/(\s\s)/gm, ' ')
    // escape double-quote with double-double-quote (see https://stackoverflow.com/q/17808511/1054723)
    .replace(/"/g, '""');
  // escape string
  csvCell = '"' + csvCell + '"';
  return csvCell;
}

export function downloadResultsAsCsv(
  results: ResultItem[],
  stem: string,
): void {
  const csv = convertResultsToCsv(results);
  downloadDataAsCsv(csv, stem);
}

export function downloadDataAsPdf(blobPart: BlobPart, stem: string): void {
  downloadOneBlob(blobPart, 'application/pdf', stem + '.pdf');
}

export function downloadDataAsCsv(blobPart: BlobPart, stem: string): void {
  downloadOneBlob(blobPart, 'text/csv;charset=utf-8', stem + '.csv');
}

/**
 * Show a default browser download menu with the blob from Axios response
 * First tries to get the name from Content-Disposition header,
 * else tries to get file extension from Content-Type header
 */
export function downloadResponseAsHeaders(
  res: AxiosResponse,
  stem: string,
): void {
  const contentType =
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    (res.headers['content-type'] || 'application/octet-stream').toString();
  const disposition = res.headers['content-disposition'];
  if (typeof disposition === 'string' && disposition.includes('attachment')) {
    // Regex from https://stackoverflow.com/a/40940790
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches !== null && matches[1] !== '') {
      stem = matches[1].replace(/['"]/g, '');
    }
  } else {
    const contentRegex = /\/(?<ext>\w{3})/;
    const matches = contentRegex.exec(contentType);
    if (matches !== null && matches[1] !== '') {
      stem = stem + '.' + matches[1];
    }
  }
  downloadOneBlob(res.data, contentType, stem);
}

export function downloadOneBlob(
  blobPart: BlobPart,
  mediaType: string,
  name: string,
): void {
  const blob = new Blob([blobPart], { type: mediaType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

export function getFileDisplayName(
  file: Pick<File, 'name'>,
  index: number,
  fallbackPrefix = 'file',
): string {
  return file.name || `${fallbackPrefix}_${index + 1}`;
}

export function getFileNamesPreview(
  fileNames: string[],
  previewLimit = 3,
): string {
  const preview = fileNames.slice(0, previewLimit).join(', ');
  if (fileNames.length <= previewLimit) {
    return preview;
  }
  return `${preview} and ${fileNames.length - previewLimit} more`;
}
